import { Music2, Play, Pause, Zap, ListPlus, ListCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { MusicTrack } from '@/lib/musicTypes';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { useTrackZaps, formatSats } from '@/hooks/useTrackZaps';
import { nip19 } from 'nostr-tools';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import { ZapButton } from './ZapButton';
import { LikeButton } from './LikeButton';
import { ShareButton } from './ShareButton';
import { AddToPlaylistDialog } from './AddToPlaylistDialog';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePlaylists } from '@/hooks/usePlaylists';
import { MUSIC_TRACK_KIND } from '@/lib/musicTypes';

interface TrackCardProps {
  track: MusicTrack;
  playlist?: MusicTrack[];
}

export function TrackCard({ track, playlist }: TrackCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const author = useAuthor(track.pubkey);
  const artistAuthor = useAuthor(track.artistPubkey || '');
  const { data: zaps } = useTrackZaps(track.event.kind, track.pubkey, track.id);
  const { currentTrack, isPlaying, playTrack } = useMusicPlayer();
  const { user } = useCurrentUser();
  const { data: playlists } = usePlaylists({ pubkey: user?.pubkey });

  const metadata = author.data?.metadata;
  const uploaderName = metadata?.name || genUserName(track.pubkey);

  // Determine the artist name to display
  const artistMetadata = artistAuthor.data?.metadata;
  const artistDisplayName = track.artistName
    || (track.artistPubkey && (artistMetadata?.name || genUserName(track.artistPubkey)))
    || uploaderName;

  // Check if track is in any playlist
  const trackRef = `${MUSIC_TRACK_KIND}:${track.pubkey}:${track.id}`;
  const isInPlaylist = playlists?.some((p) => p.trackRefs.includes(trackRef)) ?? false;

  // Calculate total zap amount
  const totalZaps = zaps?.reduce((sum, zap) => sum + zap.amount, 0) || 0;

  // Create naddr for the track
  const naddr = nip19.naddrEncode({
    kind: track.event.kind,
    pubkey: track.pubkey,
    identifier: track.id,
  });

  // Generate gradient background if no image
  const backgroundStyle = track.imageUrl
    ? { backgroundImage: imageLoaded ? `url(${track.imageUrl})` : 'none' }
    : track.gradient
    ? { background: `linear-gradient(135deg, ${track.gradient.join(', ')})` }
    : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };

  const isCurrentTrack = currentTrack?.event.id === track.event.id;
  const isCurrentlyPlaying = isCurrentTrack && isPlaying;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't play if clicking on a link or button
    const target = e.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) {
      return;
    }
    playTrack(track, playlist);
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    playTrack(track, playlist);
  };

  return (
    <Card
      className="group overflow-hidden hover:bg-white/[0.07] transition-all cursor-pointer border-0 bg-transparent"
      onClick={handleCardClick}
    >
      <div className="flex items-center gap-4 p-2">
        {/* Album Art / Gradient - Smaller */}
        <div
          className="relative w-16 h-16 flex-shrink-0 rounded-lg bg-cover bg-center bg-white/[0.08]"
          style={backgroundStyle}
        >
          {track.imageUrl && (
            <img
              src={track.imageUrl}
              alt={track.title}
              className="absolute inset-0 w-full h-full object-cover rounded-lg opacity-0 transition-opacity duration-300"
              style={{ opacity: imageLoaded ? 1 : 0 }}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(false)}
            />
          )}
          {!track.imageUrl && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
              <Music2 className="h-8 w-8 text-white/80" />
            </div>
          )}
        </div>

        {/* Track Info */}
        <div className="flex-1 min-w-0">
          <Link
            to={`/${naddr}`}
            className="block"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-base line-clamp-1 hover:text-primary transition-colors">
              {track.title}
            </h3>
          </Link>
          {track.artistPubkey ? (
            <Link
              to={`/${nip19.npubEncode(track.artistPubkey)}`}
              className="text-sm text-muted-foreground line-clamp-1 hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {artistDisplayName}
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground line-clamp-1">
              {artistDisplayName}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Zap Total Badge */}
          {totalZaps > 0 && (
            <Badge variant="secondary" className="text-xs font-semibold">
              <Zap className="h-3 w-3 mr-1 fill-current" />
              {formatSats(totalZaps)}
            </Badge>
          )}

          {/* Like Button */}
          <div
            className="flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <LikeButton
              event={track.event}
              size="icon"
              variant="ghost"
              className="bg-white/[0.07] hover:bg-white/[0.12]"
            />
          </div>

          {/* Share Button */}
          <div
            className="flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <ShareButton
              track={track}
              size="icon"
              variant="ghost"
              className="rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
            />
          </div>

          {/* Add to Playlist Button */}
          {user && (
            <div
              className="flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <AddToPlaylistDialog
                track={track}
                trigger={
                  <Button
                    size="icon"
                    variant="ghost"
                    className={`rounded-full ${
                      isInPlaylist
                        ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-500'
                        : 'bg-white/[0.07] hover:bg-white/[0.12]'
                    }`}
                  >
                    {isInPlaylist ? (
                      <ListCheck className="h-4 w-4" />
                    ) : (
                      <ListPlus className="h-4 w-4" />
                    )}
                  </Button>
                }
              />
            </div>
          )}

          {/* Zap Button */}
          <div
            className="flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <ZapButton
              event={track.event}
              size="icon"
              variant="ghost"
              className="rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
            />
          </div>

          {/* Play Button */}
          <Button
            size="icon"
            variant="ghost"
            onClick={handlePlayClick}
            className="h-10 w-10 rounded-full flex-shrink-0 border-0 bg-white text-black hover:bg-white/90 [&_svg]:!text-black"
          >
            {isCurrentlyPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4 ml-0.5" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}
